<template>
    <Head title="WorkIQ Saved Snapshots" />
    <div class="flex flex-col md:flex-row min-h-screen bg-surface text-ink">
        <aside class="md:w-60 flex-shrink-0 border-b md:border-b-0 md:border-r border-nav-border bg-nav-bg flex flex-col px-3 py-4">
            <BaseButton
                theme="neutral"
                size="sm"
                aria-label="Back to playground"
                class="self-start flex items-center gap-1"
                data-testid="workiq-snapshots-back-button"
                @click="router.visit(workiq_playground_url)">
                <i class="bi bi-chevron-left icon-bold" aria-hidden="true"></i>
                <span class="body-small">Playground</span>
            </BaseButton>

            <div class="flex items-center gap-2 px-3 pt-5 pb-3">
                <i class="bi bi-archive text-ink-muted" aria-hidden="true"></i>
                <span class="body-bold text-ink">Saved snapshots</span>
            </div>

            <p class="px-3 body-small text-ink-muted">Inspect the WorkIQ facts available to chat POCs, or delete a snapshot before regenerating it in the playground.</p>
        </aside>

        <main class="min-w-0 flex-1 overflow-y-auto">
            <div class="mx-auto max-w-5xl flex flex-col gap-5 px-5 md:px-10 py-10">
                <div class="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div class="min-w-0">
                        <BasePageTitle title="Saved WorkIQ snapshots" />
                        <p class="body-small text-ink-muted mt-1">
                            Only snapshots belonging to your user and organization are shown. Deleting one does not call WorkIQ or start a new run.
                        </p>
                    </div>
                    <div class="flex items-center gap-2">
                        <BaseButton theme="secondary" size="sm" data-testid="workiq-seed-history-button" :disabled="confirming || loading" @click="requestSeedHistory">
                            {{ seeding ? "Seeding…" : "Seed demo history" }}
                        </BaseButton>
                        <BaseButton theme="tertiary" size="sm" :disabled="loading" @click="loadSnapshots">
                            {{ loading ? "Loading…" : "Refresh" }}
                        </BaseButton>
                    </div>
                </div>

                <BaseNotification v-if="error" theme="error" :text="error" @dismiss="error = null" />
                <BaseNotification v-if="notice" theme="success" :text="notice" @dismiss="notice = null" />

                <BaseCard padding="sm" class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div class="flex min-w-0 flex-col gap-1">
                        <span class="body-small-bold text-ink">Pillar</span>
                        <BaseSelect v-model="pillarSelection" aria-label="Pillar" :options="pillarOptions" />
                    </div>
                    <div class="body-small text-ink-muted">{{ filteredSnapshots.length }} of {{ snapshots.length }} snapshot{{ snapshots.length === 1 ? "" : "s" }}</div>
                </BaseCard>

                <div v-if="loading && !loaded" class="body-small text-ink-muted">Loading saved snapshots…</div>
                <div
                    v-else-if="loaded && !snapshots.length"
                    data-testid="workiq-snapshots-empty"
                    class="rounded-xl border-2 border-dashed border-border-strong bg-surface-raised p-10 text-center">
                    <div class="body-bold text-ink">No saved snapshots</div>
                    <p class="body-small text-ink-muted mt-1">Run a pillar ask in the WorkIQ playground to create one.</p>
                </div>
                <div v-else-if="loaded && !filteredSnapshots.length" class="body-small text-ink-muted">No snapshots match this pillar.</div>

                <BaseCard v-for="snapshot in filteredSnapshots" :key="snapshot.id" as="article" :data-testid="`workiq-snapshot-${snapshot.id}`" class="flex flex-col gap-4">
                    <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div class="min-w-0 flex flex-col gap-1">
                            <div class="flex flex-wrap items-center gap-2">
                                <span class="body-bold text-ink">{{ pillarLabel(snapshot.pillar) }}</span>
                                <StatusChip :state="snapshot.is_current ? 'active' : 'neutral'" size="2xs">
                                    {{ snapshot.is_current ? "Current" : "Expired" }}
                                </StatusChip>
                                <BaseChip v-if="snapshot.distilled?.length" size="2xs"> {{ snapshot.distilled.length }} distilled </BaseChip>
                            </div>
                            <div class="body-small text-ink-muted break-words">{{ scopeSummary(snapshot) }}</div>
                            <div class="text-xs text-ink-muted break-all">{{ snapshot.scope_key }}</div>
                        </div>
                        <BaseButton theme="danger" size="sm" :data-testid="`workiq-snapshot-delete-${snapshot.id}`" :disabled="confirming" @click="requestDeleteSnapshot(snapshot)">
                            {{ deletingId === snapshot.id ? "Deleting…" : "Delete" }}
                        </BaseButton>
                    </div>

                    <dl class="grid grid-cols-1 gap-2 body-small sm:grid-cols-3">
                        <div>
                            <dt class="text-ink-muted">Prepared period</dt>
                            <dd class="font-medium text-ink">{{ snapshot.prepared_period }}</dd>
                        </div>
                        <div>
                            <dt class="text-ink-muted">Updated</dt>
                            <dd class="font-medium text-ink">{{ formatDate(snapshot.updated_at) }}</dd>
                        </div>
                        <div>
                            <dt class="text-ink-muted">Expires</dt>
                            <dd class="font-medium text-ink">{{ formatDate(snapshot.expires_at) }}</dd>
                        </div>
                    </dl>

                    <BaseCollapsible class="text-ink">
                        <template #trigger><span class="body-small-semibold text-ink-muted">Factual summary</span></template>
                        <template #content><MarkdownText :text="snapshot.summary" class="prose prose-sm max-w-none mt-2 rounded-lg bg-surface-sunken p-3" /></template>
                    </BaseCollapsible>

                    <BaseCollapsible v-if="snapshot.distilled?.length" class="text-ink">
                        <template #trigger><span class="body-small-semibold text-ink-muted">Optional distilled output</span></template>
                        <template #content>
                            <pre class="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-surface-sunken p-3 details font-mono">{{
                                pretty(snapshot.distilled)
                            }}</pre>
                        </template>
                    </BaseCollapsible>

                    <BaseCollapsible class="text-ink">
                        <template #trigger><span class="body-small-semibold text-ink-muted">Scope metadata</span></template>
                        <template #content>
                            <pre class="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-surface-sunken p-3 details font-mono">{{
                                pretty(snapshot.scope_metadata)
                            }}</pre>
                        </template>
                    </BaseCollapsible>
                </BaseCard>
            </div>
        </main>
    </div>
    <ConfirmationDialog
        v-model:open="confirmationOpen"
        :title="confirmationTitle"
        :text="confirmationText"
        :confirm-label="confirming ? 'Working…' : pendingAction?.kind === 'delete' ? 'Delete snapshot' : 'Seed history'"
        confirm-theme="danger"
        :confirm-disabled="confirming"
        :cancel-disabled="confirming"
        :close-on-confirm="false"
        @confirm="confirmPendingAction">
        <template #content>
            <p role="status" class="body-small text-ink-muted empty:sr-only">
                <template v-if="confirming">{{ deletingId ? "Deleting snapshot…" : "Seeding history…" }}</template>
            </p>
            <BaseNotification v-if="confirmationError" theme="error" hide-dismiss :text="confirmationError" />
        </template>
    </ConfirmationDialog>
</template>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseCollapsible from "~vue/components/BaseCollapsible.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { formatDate as localizedDate } from "~vue/utils/formatDate";
import { computed, onMounted, ref } from "vue";

interface Snapshot {
    id: string;
    pillar: string;
    scope_key: string;
    prepared_period: string;
    scope_metadata: Record<string, unknown>;
    summary: string;
    distilled: Record<string, unknown>[] | null;
    expires_at: string | null;
    is_current: boolean;
    created_at: string;
    updated_at: string;
}

const props = withDefaults(
    defineProps<{
        workiq_playground_url?: string;
        workiq_snapshots_api_url?: string;
        workiq_seed_history_api_url?: string;
    }>(),
    {
        workiq_playground_url: "/admin/workiq/playground/",
        workiq_snapshots_api_url: "/workiq/api/snapshots/",
        workiq_seed_history_api_url: "/workiq/api/snapshots/seed-history/",
    },
);

const SEED_WEEKS = 6;

const PILLAR_LABELS: Record<string, string> = {
    my_work: "My Work",
    journeys: "Journeys",
    collaboration_profiles: "Collaboration Profiles",
    performance_management: "Performance Review Evidence",
    collaboration_hub: "Team Dynamics",
};

const snapshots = ref<Snapshot[]>([]);
const loading = ref(false);
const loaded = ref(false);
const deletingId = ref<string | null>(null);
const seeding = ref(false);
const error = ref<string | null>(null);
const notice = ref<string | null>(null);
const pillarFilter = ref("");
const pendingAction = ref<{ kind: "delete"; snapshot: Snapshot } | { kind: "seed" } | null>(null);
const confirmationError = ref<string | null>(null);
const confirming = computed(() => deletingId.value !== null || seeding.value);
const confirmationOpen = computed({
    get: () => pendingAction.value !== null,
    set: (isOpen: boolean) => {
        // Keep the request's target stable until the asynchronous work settles.
        if (!isOpen && !confirming.value) {
            pendingAction.value = null;
            confirmationError.value = null;
        }
    },
});
const confirmationTitle = computed(() => (pendingAction.value?.kind === "delete" ? `Delete ${pillarLabel(pendingAction.value.snapshot.pillar)} snapshot` : "Seed demo history"));
const confirmationText = computed(() =>
    pendingAction.value?.kind === "delete"
        ? `Delete the ${pillarLabel(pendingAction.value.snapshot.pillar)} snapshot for ${scopeSummary(pendingAction.value.snapshot)}?`
        : `Seed ${SEED_WEEKS} weeks of demo My Work snapshot history for your user? This overwrites your recent my_work/self snapshots.`,
);

const availablePillars = computed(() => [...new Set(snapshots.value.map((snapshot) => snapshot.pillar))].sort());
const pillarSelection = computed({
    get: () => pillarFilter.value || "all",
    set: (value: string) => (pillarFilter.value = value === "all" ? "" : value),
});
const pillarOptions = computed(() => [{ key: "all", label: "All pillars" }, ...availablePillars.value.map((pillar) => ({ key: pillar, label: pillarLabel(pillar) }))]);
const filteredSnapshots = computed(() => snapshots.value.filter((snapshot) => !pillarFilter.value || snapshot.pillar === pillarFilter.value));

function pillarLabel(pillar: string): string {
    return PILLAR_LABELS[pillar] || pillar;
}

function formatDate(iso: string | null): string {
    return iso ? localizedDate(iso, "datetime") : "—";
}

function pretty(value: unknown): string {
    return JSON.stringify(value, null, 2);
}

function scopeSummary(snapshot: Snapshot): string {
    const parts = Object.entries(snapshot.scope_metadata || {})
        .map(([key, value]) => {
            const rendered = Array.isArray(value) ? value.join(", ") : String(value ?? "");
            return rendered ? `${key}: ${rendered}` : "";
        })
        .filter(Boolean);
    return parts.length ? parts.join(" · ") : snapshot.scope_key;
}

async function loadSnapshots() {
    if (loading.value) {
        return;
    }
    loading.value = true;
    error.value = null;
    try {
        const { data, response } = await useFetch(props.workiq_snapshots_api_url).get().json<{ snapshots?: Snapshot[]; error?: string }>();
        if (response.value?.ok) {
            snapshots.value = data.value?.snapshots || [];
        } else {
            error.value = data.value?.error || "Failed to load saved snapshots.";
        }
    } finally {
        loading.value = false;
        loaded.value = true;
    }
}

function requestDeleteSnapshot(snapshot: Snapshot) {
    if (confirming.value) {
        return;
    }
    confirmationError.value = null;
    pendingAction.value = { kind: "delete", snapshot };
}

function requestSeedHistory() {
    if (confirming.value) {
        return;
    }
    confirmationError.value = null;
    pendingAction.value = { kind: "seed" };
}

async function confirmPendingAction() {
    const action = pendingAction.value;
    if (!action || confirming.value) {
        return;
    }
    confirmationError.value = null;
    if (action.kind === "delete") {
        await deleteSnapshot(action.snapshot);
    } else {
        await seedHistory();
    }
}

async function deleteSnapshot(snapshot: Snapshot) {
    deletingId.value = snapshot.id;
    error.value = null;
    notice.value = null;
    try {
        const deleteUrl = `${props.workiq_snapshots_api_url}${snapshot.id}/`;
        const { data, response } = await useFetch(deleteUrl).delete().json<{ deleted?: string; error?: string }>();
        if (response.value?.ok && data.value?.deleted === snapshot.id) {
            snapshots.value = snapshots.value.filter((item) => item.id !== snapshot.id);
            notice.value = `${pillarLabel(snapshot.pillar)} snapshot deleted. You can regenerate it from the Pipeline page.`;
            pendingAction.value = null;
        } else {
            error.value = data.value?.error || "Failed to delete the snapshot.";
            confirmationError.value = error.value;
        }
    } finally {
        deletingId.value = null;
    }
}

async function seedHistory() {
    seeding.value = true;
    error.value = null;
    notice.value = null;
    try {
        const { data, response } = await useFetch(props.workiq_seed_history_api_url).post({ weeks: SEED_WEEKS }).json<{ seeded?: number; created?: number; error?: string }>();
        if (response.value?.ok) {
            notice.value = `Seeded ${data.value?.seeded ?? SEED_WEEKS} weeks of My Work history. Open a My Work trajectory to see the trend.`;
            await loadSnapshots();
            pendingAction.value = null;
        } else {
            error.value = data.value?.error || "Failed to seed snapshot history.";
            confirmationError.value = error.value;
        }
    } finally {
        seeding.value = false;
    }
}

onMounted(loadSnapshots);
</script>
